import { useNavigate } from 'react-router-dom'
import React, { useState, useEffect } from 'react'

import { Box, alpha } from '@mui/material'
import { ActionMenu } from 'components/UI/ActionMenu'
import type { ActionMenuItem } from 'components/UI/ActionMenu'
import { DrawerPopup } from 'components/UI/DrawerPopup'
import { FIELD_BORDER_FOCUS, FIELD_BORDER_HOVER } from 'components/UI/fieldMetrics'
import { ReportMissingDrawer } from 'components/pages/certificates/productPage/ReportMissingDrawer'
import { TransferOwnership } from 'components/pages/certificates/productPage/TransferOwnership'
import { GiftCertificate } from 'components/pages/certificates/productPage/GiftCertificate'
import { IN_FLIGHT_TRANSFER_STATUSES } from 'utils/certificateUtils'

import { ROUTES } from 'routes'

import type { CertificateCustomer } from 'types'

import { MoreVertical, Info, Eye, Gift, Share2, OctagonAlert, CircleHelp } from 'lucide-react'

import { helpNavState } from 'pages/Help/navState'

interface CertificatesMenuProps {
  certificate: CertificateCustomer
  buttonTopPosition?: string
  buttonRightPosition?: string
}

export const CertificatesMenu = ({
  certificate,
  buttonTopPosition = '0px',
  buttonRightPosition = '0px',
}: CertificatesMenuProps) => {
  const navigate = useNavigate()

  const [openTransferOwnership, setOpenTransferOwnership] = useState<boolean>(false)
  const [openGiftCertificate, setOpenGiftCertificate] = useState<boolean>(false)
  const [openReportMissing, setOpenReportMissing] = useState<boolean>(false)


  // VERA-304: reuse the SHARED status list rather than re-listing the strings here —
  // a second copy is where the two drift, and `PendingTransferNotification` already
  // reads the same constant.
  //
  // NOTE the field: these are CERTIFICATE statuses (`transfer_initiated`), NOT
  // `transfer.status` (which is `initiated` / `success` / `canceled`). Reading
  // `certificate.transfer.status` here would never match any entry and the guard
  // would silently do nothing — the failure mode being that Gift keeps showing and
  // nothing looks broken in the code.
  const hasInFlightTransfer = IN_FLIGHT_TRANSFER_STATUSES.includes(
    certificate.status as (typeof IN_FLIGHT_TRANSFER_STATUSES)[number],
  )

  // The in-app route to this certificate. Built from ROUTES + the id, NEVER from
  // `window.location`.
  //
  // This app renders inside a shadow root on a `MemoryRouter` (see index.tsx), so the
  // browser URL never changes as you move between screens: `window.location.href` is
  // the HOST page's address and bears no relation to the router's current path. The
  // old form, `window.location.href = \`${window.location.href}/${certificate.id}\``,
  // therefore did two wrong things at once — it built the wrong address, and it
  // navigated by full page load rather than by routing. Measured on the dev server:
  // it produced `http://localhost:5207//<id>`, and assigning that reloaded the app,
  // at which point MemoryRouter ignored the pathname (it is not in
  // `STANDALONE_ENTRY_PATHS`) and booted at `/` — so `Details` dropped the user back
  // on the certificates list they started from. In an embedded install the same
  // expression yields the brand's storefront URL with an id glued on.
  const certificateRoute = ROUTES.CERTIFICATE.replace(':certificateId', certificate.id)
  // Built from ROUTES for the same reason as `certificateRoute`. Two call sites now
  // (the `Share` row and the report-missing drawer's Share button), so it is one const
  // rather than the hand-built string the `Share` row used to carry.
  const certificateShareRoute = ROUTES.CERTIFICATE_SHARE.replace(':certificateId', certificate.id)

  // The PUBLIC certificate address. `certificate.public_url` is the ONLY source: the
  // old `window.location`-derived fallback is gone, for the reason spelled out on
  // `certificateRoute` above — under MemoryRouter in a shadow root the browser URL is
  // the HOST page's, so it bore no relation to this certificate. Measured before the
  // fix, on the dev server: Preview opened `http://localhost:5207//<id>`.
  //
  // The `public_url` value itself was VERIFIED against the API, not trusted from the
  // type — and the type is wrong. `types/Certificate.ts` declares `public_url: string`
  // (non-optional), but the contract is looser: the entity is `string | null`
  // (api/src/domain/entities/certificate.entity.ts:61) and `ICertificate` has it
  // `?: string | null`. What the API returns in practice is always a string —
  // `CertificateService.buildCertificateUrl` (api/src/domain/services/certificate
  // .service.ts:34) has no null return path — but it returns one of TWO things:
  //
  //   1. the brand's `companies_settings.view_certificate_url` template, an absolute
  //      URL. Only ever populated by the Shopify integration or a manual
  //      company-settings update; the seed leaves the column null.
  //   2. otherwise `/api/certificates/common/<id>` — a RELATIVE path to a route that
  //      DOES NOT EXIST. The only `@Controller('common')` is `/api/common/*`
  //      (countries + fonts, auth-guarded). Measured against the dev API: 404.
  //
  // So "populated" is NOT the same as "openable", and every certificate in the current
  // dev data is case 2. Passing case 2 to `window.open` resolves it against the HOST
  // page's origin and lands on a 404 — wrong in exactly the way the old fallback was
  // wrong, only less visibly. VERA-298 hit this same trap from the Share side and
  // documented it (api/src/infrastructure/controllers/certificates.customer
  // .controller.ts:426), choosing the Vera-owned `/s/:slug` permalink instead.
  //
  // Hence absolute-only, and the row HIDES otherwise (see `visible` below) rather than
  // opening something that is not this certificate.
  const publicCertificateLink = certificate.public_url
  const hasPublicCertificateLink = /^https?:\/\//i.test(publicCertificateLink ?? '')

  // VERA-315 deferred `Sell` and `Share settings`; VERA-298 now owns Share settings,
  // so it is re-added here on VERA-315's shared ActionMenu. `Sell` stays deferred.
  const menuItems: ActionMenuItem[] = [
    { label: 'Details', icon: <Info size={18} />, onClick: () => navigate(certificateRoute) },
    // "Preview", not "Preview public view".
    //
    // It briefly carried the longer label on a §44 argument (the Share page calls
    // this object the "Public view", so name it the same). That misapplies §44,
    // which is about DEFINITIONS drifting between surfaces — the Share page names
    // the OBJECT, this row names the ACTION, and they do not have to be the same
    // string. Seen in place, the cost was obvious: every other row here is one word
    // (Details / Gift / Share), so three words broke the column's rhythm to
    // disambiguate something the eye icon and the context already settle.
    {
      label: 'Preview',
      icon: <Eye size={18} />,
      // An EXTERNAL absolute URL, so it opens in a new tab rather than routing.
      onClick: () => window.open(publicCertificateLink, '_blank'),
      // §46: this is RECORD STATE — the brand has not configured a public
      // certificate address — so it hides rather than showing disabled. A disabled
      // row must carry a tooltip saying what to do about it, and there is nothing
      // this certificate's OWNER could do: the missing setting
      // (`companies_settings.view_certificate_url`) belongs to the brand, and a
      // consumer has no admin to ask. That is §46's stated carve-out for a block
      // nobody in this context can ever resolve — hide, do not make a false promise.
      //
      // §46's other half — hiding obliges the RESOLVING action to be reachable — is
      // not satisfiable here and is not being papered over: there is no public
      // certificate page to reach at all when the brand has not set one. Showing an
      // enabled row that opens a 404 would be strictly worse.
      visible: hasPublicCertificateLink,
    },
    {
      label: 'Gift',
      icon: <Gift size={18} />,
      onClick: () => setOpenGiftCertificate(true),
      // VERA-304 / §46: an item already mid-transfer cannot be gifted again, and
      // that is RECORD STATE, so it HIDES rather than showing disabled. (§46's
      // axis: permission → disabled + tooltip; record state → hidden.) Offering
      // Gift here was a dead end — pressing it opens a drawer whose send would be
      // refused by the server, and there is nothing the user could ask anyone to
      // change; the state is theirs to resolve.
      //
      // §46's obligation — "hiding an action means the action that RESOLVES that
      // state must be reachable in that state" — is met by the certificate detail
      // page, which carries `Cancel gift` (sender) / `Claim` + `Deny` (recipient)
      // as its sticky primary while the transfer is live. Reaching it is one tap:
      // the card itself. See the note below about kebab↔detail parity.
      visible: !hasInFlightTransfer,
    },
    // `Copy link` was removed here: it duplicated `Share`. Share pushes to the Share
    // page, whose every view row carries an inline Copy link AND a kebab Share that
    // falls back to the clipboard when `navigator.share` is absent (desktop) — see
    // `pages/SharePage/index.tsx`. So the desktop "I just want the URL" case is
    // covered without this row, which additionally copied the host-page-derived
    // address rather than the public one whenever the app was not in an iframe.
    {
      label: 'Share',
      icon: <Share2 size={18} />,
      onClick: () => navigate(certificateShareRoute),
    },
    {
      label: 'Report missing',
      icon: <OctagonAlert size={18} />,
      onClick: () => setOpenReportMissing(true),
      visible: certificate.status === 'active',
    },
    /*
      VRG-418 — Help, LAST in the menu.

      Position is the point: every row above it acts on THIS certificate, and Help does
      not. Putting it among them would make the set read as five things you can do to the
      item, one of which mysteriously leaves. Last, it reads as the utility it is.

      Navigating to a route from a menu is safe here — `ActionMenu` closes before it calls
      `onClick` — so nothing is left open behind the new page. §21: it takes a leading
      icon like every other row.
    */
    {
      label: 'Help',
      icon: <CircleHelp size={18} />,
      onClick: () =>
        navigate(ROUTES.HELP, { state: helpNavState('Certificate', certificateRoute) }),
    },
  ]

  return (
    <>
      <ActionMenu
        items={menuItems}
        trigger={
          <Box
            role="button"
            tabIndex={0}
            aria-label="More options"
            sx={theme => ({
              /*
               * A bare ⋮ glyph on white is not a visible affordance: its only
               * "I am a control" signal was a HOVER background, and on the phone
               * this card is designed for there is no hover at all. So it gets a
               * resting surface, which reads as a button on sight without adding
               * colour that would compete with the card's own content.
               *
               * Aligned to the certificate hero's floating controls (VRG-774):
               * same 32px box, same soft edge, same tenant radius token. Both are
               * the same thing — a control floating on a photograph the BRAND
               * uploaded — so they should not be two sizes and two radii.
               *
               * The fill stays OPAQUE while the hero's is white@0.60 glass, and
               * that divergence is deliberate. This glyph is `text.secondary`
               * (#707070), not black. On an opaque plate that is 4.95:1 whatever
               * the photo is; on white@0.60 it becomes image-dependent and
               * computes to 1.92:1 over a dark hero — under WCAG's 3:1 non-text
               * floor. Glass here would therefore force the glyph to black, which
               * makes a secondary affordance louder on a grid of twelve cards.
               * The hero can afford glass because its glyph already IS black.
               */
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // The tenant's Studio radius, not a hardcoded 8 — a brand that
              // chose square was getting square chrome on the hero and rounded
              // chrome on its cards.
              borderRadius: `${theme.shape.borderRadius}px`,
              /*
               * `!important`, inside the shorthand. `index.css` applies
               * `#vera-shadow-root * { border-color: … }` at ID specificity, so
               * the previous bare `1px solid #E4E4E7` had its COLOUR discarded —
               * it was one of the "~20 border colours in source that never
               * render" that file documents. Without this the softer edge would
               * be authored and silently dropped, exactly as its predecessor was.
               */
              border: `1px solid ${alpha(theme.palette.common.black, 0.06)} !important`,
              backgroundColor: '#FFFFFF',
              color: 'text.secondary',
              cursor: 'pointer',
              transition: 'background-color .15s, color .15s, border-color .15s',
              '&:hover': {
                backgroundColor: '#FAFAFA',
                border: `1px solid ${FIELD_BORDER_HOVER} !important`,
                color: 'text.primary',
              },
              /*
                Keyboard focus darkens the border. It used to add the same grey
                halo the search field beside it carried
                (`0 0 0 3px rgba(0,0,0,0.06)`, byte-identical) — that halo is gone
                app-wide.

                `:focus-visible`, not `:focus-within`, so this cannot spread the
                shorthand from `fieldFocusSx`: this is a keyboard-only cue on a
                button, not a field that lights up whenever anything inside it is
                focused. Only the VALUES are shared, which is the part that must
                not drift. The `!important` shorthand is required for the same
                reason documented on `fieldFocusSx` — a bare `borderColor` here
                loses to `index.css`'s `#vera-shadow-root *` on ID specificity and
                renders nothing.
              */
              '&:focus-visible': {
                outline: 'none',
                border: `1px solid ${FIELD_BORDER_FOCUS} !important`,
                boxShadow: 'none',
              },
            })}
          >
            <MoreVertical size={20} />
          </Box>
        }
      />

      {openTransferOwnership && (
        <DrawerPopup
          title="Transfer ownership"
          child={<TransferOwnership certificate={certificate} closeTransfer={() => setOpenTransferOwnership(false)} />}
          handleOpen={openTransferOwnership}
          handleClose={() => setOpenTransferOwnership(false)}
        />
      )}

      {openGiftCertificate && (
        <DrawerPopup
          title="Gift certificate"
          child={<GiftCertificate certificate={certificate} closeGift={() => setOpenGiftCertificate(false)} />}
          handleOpen={openGiftCertificate}
          handleClose={() => setOpenGiftCertificate(false)}
        />
      )}



      <ReportMissingDrawer 
        open={openReportMissing} 
        onClose={() => setOpenReportMissing(false)} 
        certificateId={certificate.id}
        onViewCertificate={() => {
          navigate(certificateRoute)
        }}
        onShare={() => {
          // Was `copyLink()`, which wrote the same host-page-derived address the
          // Preview row used — so the drawer's Share button put
          // `http://localhost:5207//<id>` on the clipboard. It is the last consumer of
          // that expression, and it goes the same way the kebab's own `Copy link` row
          // already went (see the note in `menuItems`): to the Share page, which is
          // this app's real sharing surface and always resolves, whether or not the
          // brand has configured a public certificate URL.
          navigate(certificateShareRoute)
        }}
      />
    </>
  )
}
